import styled from "@emotion/styled";
import { motion } from "framer-motion";
import type { ComponentProps } from "react";

import { paper, fonts, shade, paperA, gloss } from "../theme";
import { CARD_RADIUS } from "./promptCard";

/*
 * The printed back of a Misquote card.
 *
 * Lifted out of `PromptEntry`, which is where it was drawn, because the deckless
 * draw needs the same object — three of them, face down, waiting to be turned. A
 * second copy would have been a second card back, and two card backs is one card
 * back too many.
 *
 * Everything on it is sized in container units, so the whole print scales with
 * the card rather than with the viewport. It used to take a `scale` prop that
 * multiplied a handful of pixel sizes, which meant the mark shrank but the border
 * inset didn't: at hand size a fixed 9px frame ate an eighth of a 107px card and
 * the thing read square, whatever its actual 88.9 × 63.5 proportions said. One
 * card back, one set of proportions, any size.
 *
 * It fills its positioned parent (`inset: 0`), so the caller owns the size and
 * the aspect ratio. Pass `layoutId` to morph it into whatever it turns into.
 */
const Back = styled(motion.div)`
    position: absolute;
    inset: 0;
    container-type: size;
    border-radius: ${CARD_RADIUS};
    background: linear-gradient(158deg, ${paper.redLight} 0%, ${paper.red} 100%);
    color: ${paper.paper};
    box-shadow:
        0 1px 0 ${gloss(0.18)} inset,
        0 0 0 1px ${shade(0.12)},
        0 10px 22px -10px ${shade(0.4)},
        0 24px 36px -22px ${shade(0.45)};
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Frame = styled.div`
    position: absolute;
    inset: 3.6cqh;
    border: 0.6cqh solid ${paperA(0.4)};
    border-radius: 2.4cqh;
    pointer-events: none;
`;

const Glyph = styled.div`
    position: absolute;
    top: -0.16em;
    left: 0.02em;
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 900;
    font-size: 78cqh;
    line-height: 1;
    color: ${paperA(0.12)};
    pointer-events: none;
`;

const Mark = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2.4cqh;
`;

/* The mark is the card, so it's allowed to be big — it was set at a fifth of the
 * card's height and read as a monogram in a lot of empty red. */
const MarkM = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 34cqh;
    line-height: 0.9;
`;

const MarkLabel = styled.div`
    font-family: ${fonts.sans};
    font-size: 4.4cqh;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${paperA(0.8)};
`;

export function PromptCardBack(props: ComponentProps<typeof Back>) {
    return (
        <Back {...props}>
            <Glyph>"</Glyph>
            <Frame />
            <Mark>
                <MarkM>M</MarkM>
                <MarkLabel>Misquote</MarkLabel>
            </Mark>
        </Back>
    );
}
